import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/view/components/ui/dialog'
import { Button } from '@/view/components/ui/button'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'
import { useToast } from '@/view/components/ui/use-toast'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { Spinner } from '@/view/components/ui/spinner'
import { customerService } from '@/app/services/customerService'
import { BasicInput } from '@/view/components/BasicInput'
import { useState, useRef, useCallback } from 'react'
import { Upload, FileX, FileCheck } from 'lucide-react'
import * as XLSX from 'xlsx'
import { format, parseISO } from 'date-fns'
import { PartnerType } from '@/app/utils/partners.enum'

const schema = z.object({
  schedule: z.string().min(1, { message: 'Campo obrigatório' }),
})

type FormValues = z.infer<typeof schema>

interface ImportDataModalProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  type?: PartnerType
}

const AMOUNT_CPFS_SCHEDULE = Number(import.meta.env.VITE_AMOUNT_CPFS_SCHEDULE)

export const ImportDataModal = ({
  isOpen,
  onOpenChange,
  type,
}: ImportDataModalProps) => {
  const [fileName, setFileName] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [cpfList, setCpfList] = useState<string[]>([])
  const [fileError, setFileError] = useState('')
  const dropzoneRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  const { toast } = useToast()
  const { register, reset, handleSubmit, formState } = form

  const resetFileState = () => {
    setFileName('')
    setCpfList([])
    setFileError('')
  }

  const extractCpfFromExcel = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        if (!e.target || !e.target.result) {
          throw new Error('Falha ao ler o arquivo')
        }

        const data = new Uint8Array(e.target.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })

        const firstSheet = workbook.Sheets[workbook.SheetNames[0]]

        const jsonData = XLSX.utils.sheet_to_json(firstSheet) as Record<
          string,
          unknown
        >[]

        if (jsonData.length === 0) {
          setFileError('Planilha vazia.')
          return
        }

        const firstRow = jsonData[0] as Record<string, unknown>
        const cpfColumnKey = Object.keys(firstRow).find(
          (key) => key.toLowerCase() === 'cpf',
        )

        if (!cpfColumnKey) {
          setFileError("Nenhuma coluna 'CPF' ou 'cpf' encontrada na planilha.")
          return
        }

        const cpfs = jsonData
          .map((row) => {
            const cpfValue = row[cpfColumnKey]
            return cpfValue ? String(cpfValue).trim().replace(/\D/g, '') : ''
          })
          .filter((cpf) => cpf)

        if (cpfs.length === 0) {
          setFileError('Nenhum CPF encontrado na coluna especificada.')
          return
        }

        setCpfList(cpfs)
        setFileError('')
      } catch (error) {
        console.error('Erro ao processar arquivo Excel:', error)
        setFileError(
          'Erro ao processar o arquivo. Verifique se é um Excel válido.',
        )
      }
    }

    reader.onerror = () => {
      setFileError('Erro ao ler o arquivo.')
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFile = (file: File | null) => {
    resetFileState()

    if (!file) return

    const validExcelTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ]

    if (!validExcelTypes.includes(file.type)) {
      setFileError(
        'Formato inválido. Por favor, selecione apenas arquivos Excel (.xlsx, .xls)',
      )
      return
    }

    setFileName(file.name)
    extractCpfFromExcel(file)
  }

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files?.length > 0) {
      handleFile(files[0])
    }
  }, [])

  const onFileBrowse = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.xlsx,.xls'
    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement
      const files = target.files
      if (files && files?.length > 0) {
        handleFile(files[0])
      }
    }
    input.click()
  }

  const { mutate: onSubmit, isPending } = useMutation({
    mutationFn: async (formData: FormValues) => {
      const parsedDate = parseISO(formData.schedule)
      const formattedDate = parsedDate.toISOString()

      const chunkSize = AMOUNT_CPFS_SCHEDULE || 500
      const totalChunks = Math.ceil(cpfList.length / chunkSize)
      let completedChunks = 0

      const requests = []

      for (let i = 0; i < cpfList.length; i += chunkSize) {
        const chunk = cpfList.slice(i, i + chunkSize)

        const importData = {
          schedule: formattedDate,
          keys: chunk,
          type,
        }

        const request = customerService
          .importCustomerData(importData)
          .then(() => {
            completedChunks++
            setProgress(Math.round((completedChunks / totalChunks) * 100))
          })

        requests.push(request)
      }

      await Promise.all(requests)

      reset({
        schedule: currentDate,
      })
      resetFileState()
      onOpenChange(false)
    },
    onSuccess: () => {
      toast({
        variant: 'default',
        title: 'Dados importados com sucesso.',
      })
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Ocorreu um erro. Tente novamente mais tarde',
      })
    },
  })

  const getErrorMessage = (error: unknown): string | undefined => {
    if (typeof error === 'string') return error
    return undefined
  }

  const currentDate = format(new Date(), 'yyyy-MM-dd')

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader className="-mt-3 border-b pb-4">
          <DialogTitle className="text-xl font-semibold text-black01">
            Agendar consulta CPFs
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <div className="space-y-6">
            <div>
              <h2 className="font-medium text-black04">Data</h2>
              <BasicInput
                {...register('schedule')}
                type="date"
                className="mt-2 h-11 w-[152px]"
                min={currentDate}
                defaultValue={currentDate}
                error={getErrorMessage(formState.errors.schedule?.message)}
              />
            </div>

            <div>
              <h2 className="font-medium text-black04">Arquivo Excel</h2>
              <div className="mt-2">
                <div
                  ref={dropzoneRef}
                  className={`cursor-pointer rounded-md border-2 border-dashed p-6 text-center transition-colors
                        ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary/50'}
                        ${fileError ? 'border-red-400 bg-red-50' : ''}
                      `}
                  onClick={onFileBrowse}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                >
                  {fileName ? (
                    <div className="flex flex-col items-center">
                      {fileError ? (
                        <FileX size={40} className="mb-2 text-red-500" />
                      ) : (
                        <FileCheck size={40} className="mb-2 text-green-500" />
                      )}
                      <p className="font-medium text-gray-700">{fileName}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        {fileError || `${cpfList.length} CPFs encontrados`}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          onFileBrowse()
                        }}
                      >
                        Trocar arquivo
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload size={40} className="mb-2 text-gray-400" />
                      <p className="font-medium text-gray-700">
                        Arraste e solte seu arquivo Excel aqui
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Ou clique para selecionar
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            className="mt-4 md:mt-0"
            disabled={isPending}
            onClick={() => {
              onOpenChange(false)
              reset({
                schedule: currentDate,
              })
              resetFileState()
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit((formData) => onSubmit(formData))}
            variant="default"
            className="bg-primary hover:bg-primary/90"
            disabled={cpfList.length === 0 || !!fileError}
          >
            {isPending ? (
              <>
                <Spinner className="mr-2 text-white" size="small" />
                {progress}%
              </>
            ) : (
              'Importar'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
